import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FormikProps } from "formik";
import dayjs from "utils/dayjs";
import { titleCase } from "title-case";
import { useLazyQuery } from "@apollo/client";
import { useFeatureEntitlements } from "@frontegg/react";
import { Button, Typography } from "@mui/material";
import { HugeiconsIcon } from "@hugeicons/react";
import { HelpCircleIcon } from "@hugeicons-pro/core-stroke-rounded";
import {
  Accordion,
  Avatar,
  Chip,
  DatePicker,
  FormField,
  Select,
  TextField,
  Tooltip,
  type SelectOption,
} from "@outpave/ds";
import { Inline, Stack } from "components/layout/reusableLayouts";
import AllocationDrawer from "components/admin/allocations/AllocationDrawer";
import AllocationSearchAutoComplete, {
  AllocationIdAndTypeProps,
} from "components/common/AllocationSearchAutoComplete";
import ExpenseCategorySearchAutoComplete from "components/common/CategorySearchAutoComplete";
import FormikCustomFields from "components/common/FormikCustomFields";
import MerchantSearchAutoComplete from "components/common/MerchantSearchAutoComplete";
import SpendingLimitFields from "components/common/cards/SpendingLimitFields";
import type { CardProgramCapabilities } from "hooks/useCardPrograms";
import CardType from "enums/CardType";
import CustomFieldParentType from "enums/CustomFieldParentType";
import PolicyRequired from "enums/PolicyRequired";
import Role from "enums/Role";
import { GET_ALLOCATION } from "graphql/allocation";
import { useCategories } from "hooks/useCategories";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import {
  CardPolicy,
  hasAtLeastOneLimit,
  Merchant,
} from "utils/cards/cardFormUtils";
import { calculateDefaultExpirationDate } from "utils/cards/defaultExpirationDate";
import { fieldErrorText, isTouchedAndError } from "utils/formikFieldError";

type Section =
  | "spendingLimits"
  | "merchantLimits"
  | "scheduleCancelation"
  | "categorizations"
  | "policy";

/** Only what the form reads off the card being edited. */
export interface PolicyFormCard {
  type?: string | null;
  pendingRequest?: {
    requestData?: { card_expiration_date?: number | null } | null;
  } | null;
  activePolicy?: { card_expiration_date?: number | null } | null;
}

interface CardPolicyFormProps {
  formik: FormikProps<CardPolicy>;
  /** A preset cancels after a time frame rather than on a date, and has no custom fields. */
  variant?: "card" | "preset";
  /** The card being edited, once loaded; absent while one is created. */
  card?: PolicyFormCard | null;
  /** The latest a new card can be set to cancel, in epoch milliseconds, from its program. */
  latestCardExpiration?: number;
  /** What the card's program can honor; an option it cannot is not offered. */
  capabilities?: Partial<CardProgramCapabilities>;
}

type FormProps = { formik: FormikProps<CardPolicy> };

type ListProps = FormProps & { label: string; helperText: string };

const LIMIT_FIELDS = [
  "transactionLimit",
  "dailyLimit",
  "weeklyLimit",
  "monthlyLimit",
  "totalLimit",
];

const hasItems = (list?: unknown[] | null) => (list?.length ?? 0) > 0;

// Keyed on the boolean, so a section the user closes stays closed until the state changes again.
const useOpenWhen = (
  open: (section: Section) => void,
  section: Section,
  when: boolean,
) =>
  useEffect(() => {
    if (when) open(section);
  }, [open, section, when]);

const MerchantList: React.FC<
  ListProps & { field: "allowedMerchants" | "blockedMerchants" }
> = ({ formik, field, label, helperText }) => {
  const { t } = useTranslation();
  const merchants = formik.values[field] ?? [];

  const add = (merchant: Merchant) => {
    if (merchants.some((item) => item.id === merchant.id)) return;
    formik.setFieldValue(field, [...merchants, merchant]);
  };

  const remove = (id: string) =>
    formik.setFieldValue(
      field,
      merchants.filter((item) => item.id !== id),
    );

  return (
    <Stack gap="var(--space-3)">
      <MerchantSearchAutoComplete
        title={label}
        helperText={helperText}
        placeholder={t("Search for Merchant")}
        onSelectMerchant={(merchant) => merchant && add(merchant)}
      />
      {merchants.length > 0 && (
        <Inline gap="var(--space-2)">
          {merchants.map((merchant) => (
            <Chip
              key={merchant.id}
              labels={{ remove: t("Remove") }}
              onClear={() => remove(merchant.id)}
              leading={
                <Avatar
                  size="sm"
                  src={merchant.logo || undefined}
                  name={merchant.name}
                />
              }
            >
              {titleCase(merchant.name.toLowerCase())}
            </Chip>
          ))}
        </Inline>
      )}
    </Stack>
  );
};

const CategoryList: React.FC<
  ListProps & { field: "allowedCategories" | "blockedCategories" }
> = ({ formik, field, label, helperText }) => {
  const { t } = useTranslation();
  const { outpaveCategories } = useCategories();
  const chosen = formik.values[field] ?? [];

  // The DS finds a label only in `options`, so a chosen category stays one while the list loads.
  const options: SelectOption[] = [
    ...outpaveCategories.map((category) => ({
      id: category.id,
      label: category.name,
    })),
    ...chosen
      .filter((item) => !outpaveCategories.some((c) => c.id === item.id))
      .map((item) => ({ id: item.id, label: item.name })),
  ];

  const choose = (ids: string[]) =>
    formik.setFieldValue(
      field,
      ids
        .map(
          (id) =>
            outpaveCategories.find((category) => category.id === id) ??
            chosen.find((item) => item.id === id),
        )
        .filter((item): item is { id: string; name: string } => !!item)
        .map(({ id, name }) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    );

  return (
    <Select
      multiple
      searchable
      label={label}
      helperText={helperText}
      multipleLabel={t("Categories")}
      displayMultipleUnder
      value={chosen.map((item) => item.id)}
      onChange={choose}
      options={options}
      fullWidth
    />
  );
};

const CancelationDate: React.FC<FormProps & { expiresAt: dayjs.Dayjs }> = ({
  formik,
  expiresAt,
}) => {
  const { t } = useTranslation();
  const { cancelationDate } = formik.values;

  return (
    <FormField
      label={t("Cancelation Date")}
      helperText={t("Date when Outpave will auto cancel the card")}
      errorText={fieldErrorText(formik, "cancelationDate")}
      fullWidth
    >
      <DatePicker
        aria-label={t("Cancelation Date")}
        value={cancelationDate ? dayjs(cancelationDate) : null}
        onChange={(date) => {
          // A half-typed date is invalid; storing it would empty the field mid-entry.
          if (date && !date.isValid()) return;
          formik.setFieldValue(
            "cancelationDate",
            date ? date.endOf("day").valueOf() : null,
          );
        }}
        onBlur={() => formik.setFieldTouched("cancelationDate", true)}
        minDate={dayjs().add(1, "day")}
        maxDate={expiresAt}
        clearable
        error={isTouchedAndError(formik, "cancelationDate")}
      />
    </FormField>
  );
};

const CancelationTimeFrame: React.FC<FormProps> = ({ formik }) => {
  const { t } = useTranslation();
  const timeFrame = formik.values.cancelationTimeFrame;

  return (
    <Inline gap="var(--space-5)" wrap={false} align="start">
      <TextField
        fullWidth
        name="cancelationTimeFrame.timeFrameValue"
        type="number"
        label={t("Time")}
        value={timeFrame?.timeFrameValue || ""}
        onChange={(event) => {
          const parsed = parseInt(event.target.value, 10);
          formik.setFieldValue(
            "cancelationTimeFrame.timeFrameValue",
            Number.isNaN(parsed) ? null : parsed,
          );
        }}
        onBlur={formik.handleBlur}
        errorText={fieldErrorText(formik, "cancelationTimeFrame")}
        inputProps={{
          min: 1,
          pattern: "[0-9]*",
          // Stop the scroll wheel incrementing the amount.
          onWheel: (event: React.WheelEvent<HTMLInputElement>) =>
            event.currentTarget.blur(),
        }}
      />
      <Select
        fullWidth
        id="timeFrameFrequency"
        label={t("Frequency")}
        clearable
        value={timeFrame?.timeFrameFrequency || null}
        onChange={(value) =>
          formik.setFieldValue("cancelationTimeFrame.timeFrameFrequency", value)
        }
        options={[
          { id: "day", label: t("Days") },
          { id: "week", label: t("Weeks") },
          { id: "month", label: t("Months") },
        ]}
      />
    </Inline>
  );
};

const Categorizations: React.FC<FormProps> = ({ formik }) => {
  const { t } = useTranslation();
  const isAdminOrHigher = useCheckUserRole(Role.Admin);
  const { isEntitled: featureAllocations } = useFeatureEntitlements(
    "feature_allocations",
  );
  const [getAllocation, { data: allocationData }] =
    useLazyQuery(GET_ALLOCATION);
  const [creatingAllocation, setCreatingAllocation] = useState(false);

  const closeAllocationDrawer = (createdId?: string) => {
    setCreatingAllocation(false);
    if (createdId) getAllocation({ variables: { id: createdId } });
  };

  useEffect(() => {
    const created = allocationData?.allocationById;
    if (!created) return;
    formik.setFieldValue("allocationType", {
      key: created.allocationType.key,
      name: created.allocationType.name,
    });
    formik.setFieldValue("allocationCode", {
      id: created.id,
      allocationCode: created.allocationCode,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allocationData]);

  useEffect(() => {
    formik.validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.allocationCode]);

  return (
    <Stack gap="var(--space-4)">
      <FormField
        label={t("Expense Category")}
        tooltip={t("ExpenseCategoryLockTooltip")}
        helperText={t("ExpenseCategoryLock")}
      >
        <ExpenseCategorySearchAutoComplete
          expenseCategory={
            formik.values.expenseCategory?.id
              ? formik.values.expenseCategory
              : null
          }
          onSelectExpenseCategory={(category) =>
            formik.setFieldValue("expenseCategory", {
              id: category?.id || "",
              name: category?.name || "",
              level: category?.level || 0,
            })
          }
        />
      </FormField>

      {featureAllocations && (
        <Stack gap="var(--space-2)">
          {/* Not a FormField: the label row also carries an action. */}
          <Inline justify="between" align="center" wrap={false}>
            <Inline gap="var(--space-2)" wrap={false}>
              <Typography
                variant="label"
                sx={{ color: "var(--color-text-secondary)" }}
              >
                {t("Allocation Type")}
              </Typography>
              <Tooltip title={t("AllocationLockTooltip")}>
                <HugeiconsIcon icon={HelpCircleIcon} size={16} />
              </Tooltip>
            </Inline>
            {isAdminOrHigher && (
              <Button
                size="small"
                variant="default-soft"
                onClick={() => setCreatingAllocation(true)}
              >
                {t("Create New Allocation")}
              </Button>
            )}
          </Inline>
          <AllocationSearchAutoComplete
            title={null}
            allocation={formik.values.allocationCode}
            allocationType={formik.values.allocationType}
            onSelectAllocation={(allocation) => {
              formik.setFieldValue("allocationCode", {
                id: allocation?.id,
                allocationCode: allocation?.allocationCode,
              });
              formik.setFieldValue(
                "allocationType",
                allocation?.allocationType,
              );
            }}
            handleBlur={() => formik.setFieldTouched("allocationCode", true)}
            errorText={fieldErrorText(formik, "allocationCode")}
          />
          <Typography
            variant="body2"
            sx={{ color: "var(--color-text-tertiary)" }}
          >
            {t("AllocationLock")}
          </Typography>
        </Stack>
      )}

      <AllocationDrawer
        open={creatingAllocation}
        handleCloseDrawer={() => closeAllocationDrawer()}
        afterAllocationSubmit={(created: AllocationIdAndTypeProps) =>
          closeAllocationDrawer(created?.allocationId)
        }
      />
    </Stack>
  );
};

const PolicySelect: React.FC<
  FormProps & {
    name: "allocationPolicy" | "receiptPolicy";
    label: string;
    helperText: string;
    tooltip: string;
    captions: [required: string, policy: string, notRequired: string];
  }
> = ({ formik, name, label, helperText, tooltip, captions }) => {
  const { t } = useTranslation();

  return (
    <Select
      label={label}
      tooltip={tooltip}
      helperText={helperText}
      clearable={false}
      value={formik.values[name]}
      onChange={(value) =>
        formik.setFieldValue(name, value ?? PolicyRequired.policy)
      }
      options={[
        {
          id: PolicyRequired.required,
          label: t("Required"),
          caption: captions[0],
        },
        { id: PolicyRequired.policy, label: t("Policy"), caption: captions[1] },
        {
          id: PolicyRequired.not_required,
          label: t("Not Required"),
          caption: captions[2],
        },
      ]}
      errorText={fieldErrorText(formik, name)}
      fullWidth
    />
  );
};

const CardPolicyForm: React.FC<CardPolicyFormProps> = ({
  formik,
  variant = "card",
  card,
  latestCardExpiration,
  capabilities,
}) => {
  const { t } = useTranslation();
  const isAdminOrHigher = useCheckUserRole(Role.Admin);
  const { isEntitled: featureMerchantLimits } = useFeatureEntitlements(
    "feature_merchant_limits",
  );
  const { isEntitled: featureAllocations } = useFeatureEntitlements(
    "feature_allocations",
  );
  const { isEntitled: featureCustomFields } = useFeatureEntitlements(
    "feature_card_custom_fields",
  );

  const { values } = formik;
  // An external card is a feed we import, so its limits and merchant rules belong to its issuer.
  const external = card?.type === CardType.EXTERNAL;

  const expiration =
    card?.pendingRequest?.requestData?.card_expiration_date ||
    card?.activePolicy?.card_expiration_date;
  // The program's expiry is a UTC instant, read in UTC as the fallback is, so a positive offset cannot move it a day on.
  const expiresAt = useMemo(() => {
    if (expiration) return dayjs(expiration);
    if (latestCardExpiration) return dayjs(latestCardExpiration).utc();
    return calculateDefaultExpirationDate();
  }, [expiration, latestCardExpiration]);
  // A new card's cancelation date is validated against the expiry the picker bounds it by, which follows its program.
  const latestCancelation =
    variant === "card" && !card ? expiresAt.valueOf() : undefined;
  const { setFieldValue } = formik;
  useEffect(() => {
    if (
      latestCancelation !== undefined &&
      values.expirationDate !== latestCancelation
    )
      setFieldValue("expirationDate", latestCancelation);
  }, [latestCancelation, values.expirationDate, setFieldValue]);

  const [expanded, setExpanded] = useState<Section[]>(["spendingLimits"]);
  const toggle =
    (section: Section) => (_event: React.SyntheticEvent, isExpanded: boolean) =>
      setExpanded((prev) =>
        isExpanded
          ? [...prev, section]
          : prev.filter((item) => item !== section),
      );
  // Adds to the open set rather than overriding `expanded`, which would swallow the user's clicks.
  const open = useCallback(
    (section: Section) =>
      setExpanded((prev) =>
        prev.includes(section) ? prev : [...prev, section],
      ),
    [],
  );

  useOpenWhen(
    open,
    "spendingLimits",
    !hasAtLeastOneLimit(values) || isTouchedAndError(formik, LIMIT_FIELDS),
  );
  useOpenWhen(
    open,
    "scheduleCancelation",
    isTouchedAndError(formik, ["cancelationDate", "cancelationTimeFrame"]),
  );
  useOpenWhen(
    open,
    "categorizations",
    isTouchedAndError(formik, ["expenseCategory", "allocationCode"]),
  );
  useOpenWhen(
    open,
    "policy",
    isTouchedAndError(formik, ["receiptPolicy", "allocationPolicy"]),
  );

  // The same either-or rules the server enforces on these four lists.
  const showAllowedMerchants =
    !!featureMerchantLimits &&
    !hasItems(values.allowedCategories) &&
    !hasItems(values.blockedMerchants);
  const showAllowedCategories =
    !hasItems(values.allowedMerchants) && !hasItems(values.blockedCategories);
  const showBlockedMerchants =
    !!featureMerchantLimits && !hasItems(values.allowedMerchants);
  const showBlockedCategories =
    !!featureMerchantLimits && !hasItems(values.allowedCategories);
  const showMerchantLimits =
    !external &&
    capabilities?.merchantLimits !== false &&
    (showAllowedMerchants ||
      showAllowedCategories ||
      showBlockedMerchants ||
      showBlockedCategories);

  // Under the default policy an Admin's own approval settles the request, so the note is for those below.
  const showApproverNote = variant === "card" && !isAdminOrHigher;
  const limitsNote = !showApproverNote
    ? t("At least one limit must be set")
    : card
      ? t("SpendingLimitsApproverNoteEdit")
      : t("SpendingLimitsApproverNote");

  const accordion = (section: Section) => ({
    expanded: expanded.includes(section),
    onChange: toggle(section),
  });

  return (
    <Stack gap="var(--space-0)">
      <Accordion
        title={t("Spending Limits")}
        description={t("How much can be spent on this card")}
        {...accordion("spendingLimits")}
      >
        <Stack gap="var(--space-4)">
          {!external && (
            <Typography
              variant="body2"
              sx={{ color: "var(--color-text-tertiary)" }}
            >
              {limitsNote}
            </Typography>
          )}
          <SpendingLimitFields
            formik={formik}
            disabled={external}
            capabilities={capabilities}
          />
        </Stack>
      </Accordion>

      {showMerchantLimits && (
        <Accordion
          title={t("Merchant Limits")}
          description={t(
            "Restrict spending to specific merchants or categories",
          )}
          {...accordion("merchantLimits")}
        >
          <Stack gap="var(--space-4)">
            {showAllowedMerchants && (
              <MerchantList
                formik={formik}
                field="allowedMerchants"
                label={t("Allowed Merchants")}
                helperText={t("Allow purchases from specific merchants")}
              />
            )}
            {showAllowedCategories && (
              <CategoryList
                formik={formik}
                field="allowedCategories"
                label={t("Allowed Merchant Categories")}
                helperText={t(
                  "Allow purchases from specific merchant categories",
                )}
              />
            )}
            {showBlockedMerchants && (
              <MerchantList
                formik={formik}
                field="blockedMerchants"
                label={t("Blocked Merchants")}
                helperText={t("Block purchases from specific merchants")}
              />
            )}
            {showBlockedCategories && (
              <CategoryList
                formik={formik}
                field="blockedCategories"
                label={t("Blocked Merchant Categories")}
                helperText={t(
                  "Block purchases from specific merchant categories",
                )}
              />
            )}
          </Stack>
        </Accordion>
      )}

      {!external && (
        <Accordion
          title={t("Schedule Cancelation")}
          description={
            variant === "preset"
              ? t("Auto cancel the card after a certain time frame")
              : t("Optionally auto-cancel this card on a set date")
          }
          {...accordion("scheduleCancelation")}
        >
          {variant === "preset" ? (
            <CancelationTimeFrame formik={formik} />
          ) : (
            <CancelationDate formik={formik} expiresAt={expiresAt} />
          )}
        </Accordion>
      )}

      <Accordion
        title={t("Categorizations")}
        description={t(
          "Set an expense category and allocation for every purchase",
        )}
        {...accordion("categorizations")}
      >
        <Categorizations formik={formik} />
      </Accordion>

      <Accordion
        title={t("Policy")}
        description={t("Choose when purchases need a receipt or an allocation")}
        {...accordion("policy")}
      >
        <Stack gap="var(--space-5)">
          {featureAllocations && (
            <PolicySelect
              formik={formik}
              name="allocationPolicy"
              label={t("Allocations")}
              helperText={t("Set custom rules for transaction allocations")}
              tooltip={t("AllocationFuturePurchasesTooltip1")}
              captions={[
                t("AllocationFuturePurchasesTooltip2"),
                t("AllocationFuturePurchasesTooltip3"),
                t("AllocationFuturePurchasesTooltip4"),
              ]}
            />
          )}
          <PolicySelect
            formik={formik}
            name="receiptPolicy"
            label={t("Receipt")}
            helperText={t("Set custom rules for transaction receipts")}
            tooltip={t("ReceiptFuturePurchasesTooltip1")}
            captions={[
              t("ReceiptFuturePurchasesTooltip2"),
              t("ReceiptFuturePurchasesTooltip3"),
              t("ReceiptFuturePurchasesTooltip4"),
            ]}
          />
        </Stack>
      </Accordion>

      {variant === "card" && featureCustomFields && (
        <FormikCustomFields
          parentType={CustomFieldParentType.CARD}
          formik={formik}
        />
      )}
    </Stack>
  );
};

export default CardPolicyForm;
